import {useCallback} from 'react';
import {TFunction} from 'i18next';
import {useGetParagraphsNextLazyQuery} from 'features/VehicleProjects/hooks/AssignedParagraphsInfinity.generated';
import {handleClickRegulationVersionDetailPage} from 'components/EntityList/Excel/XLRegulationVersionDetailPage';
import {RegulatoryDocumentParagraph} from 'types';

type UserRoles = {
	isAdmin: boolean;
	isVko: boolean;
	isVex: boolean;
	isReader: boolean;
	isBedarfskoordinator: boolean;
};

type GetRegDocParagraphIds = (options: {
	variables: {regulatoryDocumentId: string};
}) => Promise<{
	data?: {
		regulatoryDocument?: {
			paragraphs?: Array<{id: string}>;
		} | null;
	};
}>;

type UseRegDocParagraphsExcelExportParams = {
	isGetexInjected: boolean;
	regulatoryDocumentId: string;
	arrIds: string[];
	allParagraphsInScope?: RegulatoryDocumentParagraph[];
	currentDocuments: RegulatoryDocumentParagraph[];
	initialSortField: string;
	normalizeParagraphId: (value: string) => string;
	normalizeParagraphIdForComparison: (value: string) => string;
	getRegDocParagraphIds: GetRegDocParagraphIds;
	getSortOrderDB: (preFilteredIds: string[], sortField: string) => Promise<string[]>;
	pageDetails: any;
	roles: UserRoles;
	tExcel: TFunction;
};

export const useRegDocParagraphsExcelExport = ({
	isGetexInjected,
	regulatoryDocumentId,
	arrIds,
	allParagraphsInScope,
	currentDocuments,
	initialSortField,
	normalizeParagraphId,
	normalizeParagraphIdForComparison,
	getRegDocParagraphIds,
	getSortOrderDB,
	pageDetails,
	roles,
	tExcel,
}: UseRegDocParagraphsExcelExportParams) => {
	const [getParagraphsNextForExport] = useGetParagraphsNextLazyQuery({
		fetchPolicy: 'network-only',
	});

	return useCallback(
		async (sortField: string, sortOrder: string) => {
			if (isGetexInjected || !pageDetails?.regulatoryDocumentDetails) {
				return;
			}

			const {data: paragraphIdsData} = await getRegDocParagraphIds({
				variables: {regulatoryDocumentId},
			});

			const idsFromServer = (
				paragraphIdsData?.regulatoryDocument?.paragraphs ?? []
			).map(paragraph => normalizeParagraphId(paragraph.id));

			const paragraphsByComparableId = new Map(
				(allParagraphsInScope ?? []).map(paragraph => [
					normalizeParagraphIdForComparison(paragraph.id),
					paragraph,
				]),
			);

			const idsFromList =
				idsFromServer.length > 0
					? idsFromServer
					: arrIds.length > 0
					? arrIds
					: currentDocuments.map(paragraph => paragraph.id);
			const effectiveSortField = sortField || initialSortField;
			const sortedIds = await getSortOrderDB(idsFromList, effectiveSortField);
			const orderedIds =
				sortOrder === 'DESC' ? sortedIds.slice().reverse() : sortedIds;

			const idsForExport = orderedIds.length > 0 ? orderedIds : idsFromList;
			const chunkSize = 50;
			const fetchedParagraphs: RegulatoryDocumentParagraph[] = [];

			for (let index = 0; index < idsForExport.length; index += chunkSize) {
				const idsChunk = idsForExport.slice(index, index + chunkSize);
				const preFilteredParagraphIds = idsChunk.map(paragraphId => ({
					regDocId: normalizeParagraphId(regulatoryDocumentId),
					paragraphId: normalizeParagraphId(paragraphId),
				}));

				const {data} = await getParagraphsNextForExport({
					variables: {
						nextParaArgs: {
							preFilteredParagraphIds,
						},
					},
				});

				fetchedParagraphs.push(
					...((data?.paragraphsNext ?? []) as RegulatoryDocumentParagraph[]),
				);
			}

			const fetchedByComparableId = new Map(
				fetchedParagraphs.map(paragraph => [
					normalizeParagraphIdForComparison(paragraph.id),
					paragraph,
				]),
			);

			const paragraphsFromScope = idsForExport
				.map(id =>
					paragraphsByComparableId.get(normalizeParagraphIdForComparison(id)),
				)
				.filter((paragraph): paragraph is RegulatoryDocumentParagraph =>
					Boolean(paragraph),
				);

			const paragraphsFromFetched = idsForExport
				.map(id =>
					fetchedByComparableId.get(normalizeParagraphIdForComparison(id)),
				)
				.filter((paragraph): paragraph is RegulatoryDocumentParagraph =>
					Boolean(paragraph),
				);

			const paragraphsForExport =
				paragraphsFromFetched.length > 0
					? paragraphsFromFetched
					: paragraphsFromScope;

			const fallbackRows =
				currentDocuments.length > 0 ? currentDocuments : allParagraphsInScope ?? [];

			await handleClickRegulationVersionDetailPage(
				paragraphsForExport.length > 0 ? paragraphsForExport : fallbackRows,
				roles,
				pageDetails,
				tExcel,
			).catch(error => {
				// eslint-disable-next-line no-console
				console.error('Error exporting regdoc paragraphs to Excel:', error);
			});
		},
		[
			isGetexInjected,
			pageDetails,
			getRegDocParagraphIds,
			regulatoryDocumentId,
			normalizeParagraphId,
			allParagraphsInScope,
			normalizeParagraphIdForComparison,
			arrIds,
			currentDocuments,
			initialSortField,
			getSortOrderDB,
			getParagraphsNextForExport,
			roles,
			tExcel,
		],
	);
};
